// frontend/src/services/glossary/glossaryOptimizerService.js
// Removed imports: import { pipeline, env } from '@huggingface/transformers';
import errorService from '../error/errorService';
import cacheService from '../cache/cacheService';
import api from '../api';
import logging from '../utils/logging';

const logger = logging ? logging.getLogger('glossaryOptimizer') : console;

// Constants
const DEFAULT_MAX_TERMS = 50; // Max terms to return after optimization

// Removed constants:
// - MODEL_ID
// - embeddingPipeline
// - isLoadingPipeline
// - loadingPromise
// - lastUsedTimestamp
// - COOLDOWN_PERIOD
// - STOP_WORDS

/**
 * Glossary Optimizer Service - Handles optimization of glossary terms for specific text
 */
class GlossaryOptimizerService {
  /**
   * Public method to initiate model preloading.
   * This is now a no-op as model loading happens on the backend.
   * @param {Function} progressCallback - Optional callback for progress updates
   * @returns {Promise<void>} Promise that resolves immediately
   * @public
   */
  async preloadModel(progressCallback = null) {
    logger.log('Model preloading is no longer needed (handled by backend)');
    if (progressCallback) {
      try {
        progressCallback({ status: 'ready', progress: 1.0 });
      } catch (e) {
        logger.error('Error in progress callback:', e);
      }
    }
    return Promise.resolve();
  }

  // Removed functions:
  // - _loadEmbeddingPipeline
  // - _generateEmbedding
  // - _cosineSimilarity
  // - _getStopWords

  /**
   * Split text into sentences for analysis (kept for display purposes)
   * @param {string} text - Text to split
   * @returns {Array<string>} Array of sentences
   * @public
   */
  splitIntoSentences(text) {
    if (!text) return [];
    const pattern = /[.!?](?:\s|$)|(?:\.{3}|\u2026)(?:\s|$)/g; // Basic sentence splitter
    const sentences = [];
    let lastIndex = 0;
    let match;
    while ((match = pattern.exec(text)) !== null) {
      sentences.push(text.substring(lastIndex, match.index + match[0].length).trim());
      lastIndex = match.index + match[0].length;
    }
    if (lastIndex < text.length) sentences.push(text.substring(lastIndex).trim());
    return sentences.filter(s => s.length > 0).map(s => s.length > 512 ? s.substring(0, 512) : s); // Limit sentence length
  }

  /**
   * Basic term frequency matching algorithm as fallback
   * @param {string} text - Text to analyze
   * @param {Array} glossary - Glossary entries
   * @param {string} sourceLanguage - Source language code
   * @param {Object} options - Options (e.g., maxTerms)
   * @returns {Array} Optimized glossary
   * @private
   */
  _basicTermMatching(text, glossary, sourceLanguage = 'en', options = {}) {
    logger.warn('Using basic term frequency matching for glossary optimization.');
    const textLower = text.toLowerCase();
    const maxTerms = options.maxTerms || DEFAULT_MAX_TERMS;

    const scores = glossary.map((term, index) => {
      const sourceLower = (term.source || "").toLowerCase();
      let count = 0;
      if (sourceLower) {
        let pos = textLower.indexOf(sourceLower);
        while (pos !== -1) {
          count++;
          pos = textLower.indexOf(sourceLower, pos + 1);
        }
      }
      return { index, count, term: term.source };
    });

    const selectedIndices = scores
      .filter(item => item.count > 0)
      .sort((a, b) => b.count - a.count)
      .slice(0, maxTerms)
      .map(item => item.index);

    const result = selectedIndices.map(index => glossary[index]);
    logger.info(`Basic matching: Optimized glossary from ${glossary.length} to ${result.length} terms.`);
    return result;
  }

  /**
   * Try to optimize glossary using the backend API endpoint.
   * @param {string} text - Text to analyze
   * @param {Array} glossary - Glossary entries
   * @param {string} sourceLanguage - Source language code
   * @param {Object} options - Options (e.g., maxTerms)
   * @returns {Promise<Array|null>} - Optimized glossary or null if API fails
   * @private
   */
  async _tryApiOptimization(text, glossary, sourceLanguage, options = {}) {
    try {
      // Log input details
      logger.log(`API Optimization request - text length: ${text.length}, glossary entries: ${glossary.length}, language: ${sourceLanguage}`);
      logger.log(`Options: ${JSON.stringify(options)}`);
      
      // Log glossary structure
      if (glossary && glossary.length > 0) {
        logger.log(`First glossary entry: ${JSON.stringify(glossary[0])}`);
        const hasSource = glossary.some(entry => entry.source);
        const hasTarget = glossary.some(entry => entry.target);
        logger.log(`Glossary has 'source' keys: ${hasSource}, has 'target' keys: ${hasTarget}`);
      }
      
      const requestPayload = {
        text: text,
        glossary,
        source_language: sourceLanguage,
        options: {
          max_terms: options.maxTerms || DEFAULT_MAX_TERMS,
          semantic_ratio: options.semanticRatio !== undefined ? options.semanticRatio : 0.7
        }
      };
      
      logger.log(`Sending API request to '/optimize-glossary-py' with payload structure: ${JSON.stringify({
        textLength: text.length,
        glossaryLength: glossary.length,
        options: requestPayload.options
      })}`);
      
      const response = await api.post('/optimize-glossary-py', requestPayload, { timeout: 30000 }); // Longer timeout for embedding computations
      
      logger.log(`API Response received: ${JSON.stringify({
        status: response.status,
        hasData: !!response.data,
        dataKeys: response.data ? Object.keys(response.data) : [],
        optimizedGlossaryLength: response.data && response.data.optimized_glossary ? response.data.optimized_glossary.length : 'N/A'
      })}`);
      
      if (response.data && Array.isArray(response.data.optimized_glossary)) {
        const optimizedGlossary = response.data.optimized_glossary;
        logger.log(`Server-side glossary optimization succeeded: ${optimizedGlossary.length} terms out of ${glossary.length} original terms`);
        
        // Log structure of optimized glossary
        if (optimizedGlossary.length > 0) {
          logger.log(`First optimized entry: ${JSON.stringify(optimizedGlossary[0])}`);
          const hasSource = optimizedGlossary.some(entry => entry.source);
          const hasTarget = optimizedGlossary.some(entry => entry.target);
          logger.log(`Optimized glossary has 'source' keys: ${hasSource}, has 'target' keys: ${hasTarget}`);
          
          // Log a few entries for debugging
          if (optimizedGlossary.length >= 3) {
            logger.log("Sample of optimized terms:");
            for (let i = 0; i < Math.min(3, optimizedGlossary.length); i++) {
              const entry = optimizedGlossary[i];
              logger.log(`  Term ${i+1}: source='${entry.source || 'N/A'}', target='${entry.target || 'N/A'}'`);
            }
          }
        }
        
        return optimizedGlossary;
      }
      
      logger.warn(`Server-side optimization returned invalid data: ${JSON.stringify(response.data)}`);
      return null;
    } catch (error) {
      logger.warn(`Server-side glossary optimization failed: ${error.message}. Proceeding with client-side fallback.`);
      logger.error(`Error details:`, error);
      errorService.reportError('API Glossary Optimization Failed', error);
      return null;
    }
  }

  /**
   * Cache key for glossary optimization results - simplified version
   * @param {string} text - Text sample (first part)
   * @param {Array} glossary - Glossary array
   * @returns {string} Cache key
   * @private
   */
  _getCacheKey(text, glossary) {
    // Helper function for simple string hashing (djb2 algorithm variant)
    const simpleHash = (str) => {
      let hash = 5381; // Initial hash value
      if (!str || typeof str !== 'string' || str.length === 0) return '0'; // Handle empty or non-string inputs
      for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i);
        hash = ((hash << 5) + hash) + char; /* hash * 33 + char */
        hash |= 0; // Convert to 32bit integer
      }
      return hash.toString(16); // Return as hex string
    };

    // Hash the entire batch source text
    const textHash = simpleHash(text); 
    
    // Create a stable and representative string from the glossary content for hashing
    const glossaryIdentityString = (glossary || [])
        .filter(g => g && typeof g.source === 'string' && typeof g.target === 'string') // Ensure valid terms
        .map(g => `${g.source.trim()}|${g.target.trim()}`) // Create a canonical string for each term
        .sort() // Sort these strings to make the final identity string order-independent
        .join('||'); // Join them with a distinct separator
    
    const glossaryHash = simpleHash(glossaryIdentityString);
    
    // Modified to remove MODEL_ID dependency, increase version number
    return `glossary_opt_v5_backend_${textHash}_${glossaryHash}`;
  }

  /**
   * Optimize a glossary for a specific text, now using the backend API.
   * @param {string} text - Text to analyze
   * @param {Array} glossary - Glossary entries ({source, target})
   * @param {string} sourceLanguage - Source language code (e.g., 'en')
   * @param {Object} options - Options: { maxTerms?: number, semanticRatio?: number (0-1), progressCallback?: Function }
   * @returns {Promise<Array>} - Optimized glossary
   * @public
   */
  async optimizeGlossary(text, glossary, sourceLanguage = 'en', options = {}) {
    const startTime = performance.now();
    const maxTerms = options.maxTerms || DEFAULT_MAX_TERMS;

    logger.log(`optimizeGlossary called with text length: ${text ? text.length : 0}, glossary entries: ${glossary ? glossary.length : 0}`);
    logger.log(`Source language: ${sourceLanguage}, options: ${JSON.stringify(options)}`);

    // Early return cases - same as before
    if (!text || !glossary || glossary.length === 0) {
      logger.log('Glossary optimization skipped: No text or glossary provided.');
      return glossary || [];
    }
    if (glossary.length <= maxTerms) {
      logger.log('Glossary optimization skipped: Glossary is already small enough.');
      return glossary;
    }

    // Check glossary structure
    logger.log('Checking glossary structure...');
    const hasSourceKey = glossary.every(entry => 'source' in entry);
    const hasTargetKey = glossary.every(entry => 'target' in entry);
    
    if (!hasSourceKey || !hasTargetKey) {
      logger.warn(`Glossary has inconsistent structure. All source keys: ${hasSourceKey}, All target keys: ${hasTargetKey}. First entry: ${JSON.stringify(glossary[0])}`);
    }

    // Check cache first
    const cacheKey = this._getCacheKey(text, glossary);
    const cachedResult = cacheService.get(cacheKey);
    if (cachedResult) {
      logger.log(`Using cached glossary optimization result with ${cachedResult.length} terms.`);
      
      // Debug the cached result
      if (cachedResult.length > 0) {
        logger.log(`First cached entry: ${JSON.stringify(cachedResult[0])}`);
        logger.log(`Keys in cached result: ${Object.keys(cachedResult[0])}`);
      }
      
      return cachedResult;
    }

    // Call progress callback if provided
    if (options.progressCallback) {
      try {
        options.progressCallback({ status: 'progress', progress: 0.1, info: 'Preparing optimization request' });
      } catch (e) {
        logger.error('Error in progress callback:', e);
      }
    }

    // Try API optimization
    logger.log('Attempting API optimization...');
    const apiResult = await this._tryApiOptimization(text, glossary, sourceLanguage, options);
    
    // Call progress callback at 100% if provided
    if (options.progressCallback) {
      try {
        options.progressCallback({ status: 'ready', progress: 1.0, info: 'Optimization complete' });
      } catch (e) {
        logger.error('Error in progress callback:', e);
      }
    }
    
    // If API succeeded, cache and return the result
    if (apiResult) {
      const endTime = performance.now();
      logger.log(`Glossary optimization via API complete in ${Math.round(endTime - startTime)}ms. Optimized from ${glossary.length} to ${apiResult.length} terms.`);
      
      // Verify the returned structure
      if (apiResult.length > 0) {
        logger.log(`API result first entry: ${JSON.stringify(apiResult[0])}`);
        
        // Check if the structure matches what's expected for highlighting
        const hasExpectedKeys = apiResult.every(entry => 'source' in entry && 'target' in entry);
        logger.log(`API result has expected keys (source & target): ${hasExpectedKeys}`);
        
        if (!hasExpectedKeys) {
          logger.warn('API returned entries missing required keys. This may cause highlighting issues!');
        }
      }
      
      cacheService.set(cacheKey, apiResult, 3600); // Cache for 1 hour
      return apiResult;
    }
    
    // Fallback to basic matching if API fails
    logger.warn('API optimization failed, falling back to basic term matching');
    const fallbackResult = this._basicTermMatching(text, glossary, sourceLanguage, { maxTerms });
    
    // Log fallback result structure
    if (fallbackResult.length > 0) {
      logger.log(`Fallback result first entry: ${JSON.stringify(fallbackResult[0])}`);
    }
    
    cacheService.set(cacheKey, fallbackResult, 1800); // Cache for 30 minutes (shorter since it's fallback)
    logger.log(`Returning ${fallbackResult.length} terms from fallback method`);
    return fallbackResult;
  }
}

export default new GlossaryOptimizerService();